import {MouseEvent, useEffect, useRef, useState} from 'react';
import {ColorStop} from '@shortsworks/ui/components/ColorGenerator/GradientGenerator/ColorStop';
import {
  GradientColor,
  GradientColorStop,
  GradientType
} from '@shortsworks/ui/components/ColorGenerator/GradientGenerator/types';

interface GradientBarProps {
  selectedColorStopIndex: number;
  gradientColor: GradientColor;
  onColorChange: (gradientColor: GradientColor) => void;
  onSelectColorStop?: (index: number) => void;
}

export function GradientBar({ selectedColorStopIndex, gradientColor, onColorChange, onSelectColorStop }: GradientBarProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [ rect, setRect ] = useState<DOMRect>(DOMRect.fromRect({ x: 0, y: 0, width: 0, height: 0 }));

  useEffect(() => {
    if (containerRef.current) {
      setRect(containerRef.current?.getBoundingClientRect());
    }
  }, []);

  const handleAddColorStop = (e: MouseEvent) => {
    const boundingRect = containerRef.current?.getBoundingClientRect()!;
    const left = e.clientX - boundingRect.left;
    const offset = Math.min(1, Math.max(0, left / boundingRect.width));

    const clone = gradientColor.clone();
    const addedColor = clone.addColorWithRandom(offset);

    const foundIndex = (clone.getSortedColors() as GradientColorStop[]).findIndex((item) => item.offset === offset && item.color === addedColor);

    onColorChange(clone);
    onSelectColorStop && onSelectColorStop(foundIndex);
  }

  const handleColorStopChange = (colorStop: GradientColorStop, index: number) => {
    const clone = gradientColor.clone();
    clone.colorStops[index]!.offset = colorStop.offset;
    onColorChange(clone);
  }

  return (
    <div className="w-full">
      <div
        ref={containerRef}
        className="w-full h-[20px] relative rounded-md border border-input"
        style={{
          background: gradientColor.toCss({ angle: 90, type: GradientType.Linear })
        }}
        onClick={handleAddColorStop}
      >
        {
          gradientColor.colorStops?.map((color, index) => {
            return (
              <ColorStop
                deletable={gradientColor.colorStops.length > 2}
                selected={selectedColorStopIndex === index}
                containerRect={rect}
                index={index}
                key={index}
                colorStop={color}
                onChange={handleColorStopChange}
                onSelect={(index: number) => {
                  onSelectColorStop && onSelectColorStop(index);
                }}
                onRemove={() => {
                  if (gradientColor.colorStops.length < 3) {
                    return;
                  }
                  const newColorObject = gradientColor.clone();
                  newColorObject.removeColor(index);
                  onColorChange(newColorObject);
                  onSelectColorStop && onSelectColorStop(index - 1);
                }}
              />
            )
          })
        }
      </div>
      <div className="w-full h-2 mt-0.5">
        <svg className="w-full h-2">
          <line x1="0" y1="100%" x2="100%" y2="100%" stroke="#D9D9D9" strokeWidth="1" />
          <line x1="0" y1="0" x2="0" y2="100%" stroke="#D9D9D9" strokeWidth="1" />
          <line x1="100%" y1="0" x2="100%" y2="100%" stroke="#D9D9D9" strokeWidth="1" />
          <line x1="50%" y1="0" x2="50%" y2="100%" stroke="#D9D9D9" strokeWidth="1" />
        </svg>
      </div>
    </div>
  )
}
